import { useState, useRef, useEffect } from "react";
import "./ContactForm.css";

/* ── Fires once when element enters viewport ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const ContactForm = () => {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [phone, setPhone]     = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus]   = useState(null);
  const [focused, setFocused] = useState(null);

  const [containerRef, containerInView] = useInView(0.15);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !message) {
      setStatus("error");
      setTimeout(() => setStatus(null), 3000);
      return;
    }

    setStatus("sending");

    const payload = { name, email, phone, subject: "Contact Form", message };

    try {
      const res  = await fetch("https://api.kemari-corp.com/contact.php", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === "success") {
        setStatus("success");
        setName(""); setEmail(""); setPhone(""); setMessage("");
        setTimeout(() => setStatus(null), 4000);
      } else {
        setStatus("error");
        setTimeout(() => setStatus(null), 3000);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setStatus("error");
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const fields = [
    { id: "name",  label: "Full name",     type: "text",  value: name,  set: setName,  required: true  },
    { id: "email", label: "Email address", type: "email", value: email, set: setEmail, required: true  },
    { id: "phone", label: "Phone number",  type: "tel",   value: phone, set: setPhone, required: false },
  ];

  return (
    <section id="contact" className="cf-root">
      <div
        ref={containerRef}
        className={`cf-container${containerInView ? " is-visible" : ""}`}
      >

        {/* ── LEFT ── */}
        <div className="cf-left cf-slide-left">
          <p className="cf-eyebrow">Contact</p>
          <h2 className="cf-headline">
            Get in touch<br />with Kemari.
          </h2>
          <p className="cf-sub">
            Have something in mind or just want to learn more?
            Fill out the form and our team will reach out shortly.
          </p>
        </div>

        {/* ── RIGHT / FORM ── */}
        <div className="cf-right cf-slide-right">
          <form className="cf-form" onSubmit={handleSubmit} noValidate>

            {fields.map(({ id, label, type, value, set, required }) => (
              <div
                key={id}
                className={`cf-field ${focused === id ? "is-focused" : ""} ${value ? "has-value" : ""}`}
              >
                <label className="cf-label" htmlFor={id}>{label}</label>
                <input
                  id={id}
                  className="cf-input"
                  type={type}
                  value={value}
                  required={required}
                  autoComplete="off"
                  onFocus={() => setFocused(id)}
                  onBlur={() => setFocused(null)}
                  onChange={(e) => set(e.target.value)}
                />
                <span className="cf-underline" />
              </div>
            ))}

            <div className={`cf-field cf-field--area ${focused === "message" ? "is-focused" : ""} ${message ? "has-value" : ""}`}>
              <label className="cf-label" htmlFor="message">Message</label>
              <textarea
                id="message"
                className="cf-textarea"
                value={message}
                required
                onFocus={() => setFocused("message")}
                onBlur={() => setFocused(null)}
                onChange={(e) => setMessage(e.target.value)}
              />
              <span className="cf-underline" />
            </div>

            <div className="cf-submit-row">
              <button
                type="submit"
                className={`cf-btn ${status === "sending" ? "is-sending" : ""}`}
                disabled={status === "sending"}
              >
                <span className="cf-btn-label">
                  {status === "sending" ? "Sending…" : "Send message"}
                </span>
                <svg className="cf-btn-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {status === "success" && (
                <span className="cf-toast cf-toast--ok">Message sent.</span>
              )}
              {status === "error" && (
                <span className="cf-toast cf-toast--err">Please check the required fields.</span>
              )}
            </div>

          </form>
        </div>

      </div>
    </section>
  );
};

export default ContactForm;